import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * BoldMarkdownPipe — Convierte markdown básico a HTML seguro.
 *
 * Soporta:
 *   - **negritas** → <strong>
 *   - ## Títulos h2, ### Títulos h3, #### Títulos h4
 *   - Tablas markdown (| col | col |)
 *   - Listas con viñetas (- ítem)
 *   - Saltos de línea
 *
 * El contenido se sanitiza escapando HTML antes de aplicar las transformaciones.
 *
 * Uso: {{ message.content | boldMarkdown }}
 */
@Pipe({
  name: 'boldMarkdown',
})
export class BoldMarkdownPipe implements PipeTransform {

  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) {
      return '';
    }

    // 1. Escapar HTML para prevenir XSS
    const escaped = value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    // 2. Procesar por bloques (líneas)
    const lines = escaped.split('\n');
    const resultLines: string[] = [];
    let inTable = false;
    let tableRows: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // --- Tablas markdown ---
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        // Detectar línea separadora (|---|---|)
        if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
          // Es la línea separadora, la omitimos (ya se maneja con thead/tbody)
          continue;
        }

        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        tableRows.push(trimmed);
        continue;
      }

      // Si estábamos en una tabla y la línea actual ya no es tabla, cerrar
      if (inTable) {
        resultLines.push(this.buildTable(tableRows));
        inTable = false;
        tableRows = [];
      }

      // --- Encabezados markdown ---
      if (trimmed.startsWith('#### ')) {
        const text = this.inlineFormat(trimmed.substring(5));
        resultLines.push(`<h4 class="chatbot-md-h4">${text}</h4>`);
        continue;
      }
      if (trimmed.startsWith('### ')) {
        const text = this.inlineFormat(trimmed.substring(4));
        resultLines.push(`<h3 class="chatbot-md-h3">${text}</h3>`);
        continue;
      }
      if (trimmed.startsWith('## ')) {
        const text = this.inlineFormat(trimmed.substring(3));
        resultLines.push(`<h2 class="chatbot-md-h2">${text}</h2>`);
        continue;
      }

      // --- Separador horizontal (---) ---
      if (/^-{3,}$/.test(trimmed)) {
        resultLines.push('<hr class="chatbot-md-hr">');
        continue;
      }

      // --- Listas con viñeta (- ítem) ---
      if (/^- /.test(trimmed)) {
        const text = this.inlineFormat(trimmed.substring(2));
        resultLines.push(`<li class="chatbot-md-li">${text}</li>`);
        continue;
      }

      // --- Línea normal: aplicar formato inline + salto de línea ---
      const formatted = this.inlineFormat(line);
      resultLines.push(formatted);
    }

    // Si el texto termina en tabla, cerrarla
    if (inTable) {
      resultLines.push(this.buildTable(tableRows));
    }

    // Agrupar <li> consecutivos en <ul>
    const html = this.wrapConsecutiveLists(resultLines).join('\n');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Aplica formato inline: **negritas**, *itálicas* y emojis se mantienen.
   */
  private inlineFormat(text: string): string {
    // Primero negritas (**texto**), luego itálicas (*texto*)
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
  }

  /**
   * Construye una tabla HTML a partir de filas markdown.
   * La primera fila se trata como encabezado (thead).
   */
  private buildTable(rows: string[]): string {
    if (rows.length === 0) return '';

    const parseRow = (row: string): string[] =>
      row
        .split('|')
        .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1)
        .map(cell => cell.trim());

    // Primera fila → thead
    const headerCells = parseRow(rows[0]);
    const thead = `<thead><tr>${headerCells
      .map(c => `<th>${this.inlineFormat(c)}</th>`)
      .join('')}</tr></thead>`;

    // Resto → tbody
    const bodyRows = rows.slice(1);
    const tbody = bodyRows.length
      ? `<tbody>${bodyRows
          .map(
            r =>
              `<tr>${parseRow(r)
                .map(c => `<td>${this.inlineFormat(c)}</td>`)
                .join('')}</tr>`
          )
          .join('')}</tbody>`
      : '';

    return `<div class="chatbot-md-table-wrapper"><table class="chatbot-md-table">${thead}${tbody}</table></div>`;
  }

  /**
   * Envuelve elementos <li> consecutivos en un <ul>.
   */
  private wrapConsecutiveLists(lines: string[]): string[] {
    const result: string[] = [];
    let inList = false;

    for (const line of lines) {
      if (line.startsWith('<li')) {
        if (!inList) {
          result.push('<ul class="chatbot-md-ul">');
          inList = true;
        }
        result.push(line);
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        result.push(line);
      }
    }

    if (inList) {
      result.push('</ul>');
    }

    return result;
  }
}
